import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Toolbar,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Tooltip,
  Alert,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  LinearProgress,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  RestartAlt as ResetIcon,
  Circle as CircleIcon,
  Block as BlockIcon,
  CheckCircle as AllowIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Tune as TuneIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CompareArrows as CompareIcon,
  Warning as WarningIcon,
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
  Link as LinkIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
} from '@mui/icons-material';
import {
  setTargetUrl,
  setSelectedScriptId,
  setSelectedRuleSetIds,
  setFiltersEnabled,
  setSimulationStatus,
  setActiveTab,
  setCompareMode,
  setTestPattern,
  setSimulationTime,
  addNetworkResult,
  clearNetworkResults,
  toggleElementRule,
  addElementRule,
  removeElementRule,
  setScriptSteps,
  updateScriptStep,
  addConflictingFilter,
  clearConflictingFilters,
  setEditorRules,
  addEditorRule,
  updateEditorRule,
  removeEditorRule,
  reorderEditorRules,
  resetSimulation,
  fetchRuleSets,
  fetchExtensionScripts,
} from '../redux/slices/filterSimulatorSlice';
import networkMonitorService from '../services/api/networkMonitorService';

// -- Demo simulation data --
const DEMO_URLS = [
  'https://app.example.com/api/v1/login',
  'https://app.example.com/api/v1/user/profile',
  'https://cdn.example.com/assets/app.bundle.js',
  'https://cdn.example.com/styles/main.css',
  'https://cdn.example.com/images/logo.png',
  'https://analytics.tracker.com/collect?v=1',
  'https://ads.network.com/pixel.gif',
  'https://fonts.googleapis.com/css2?family=Roboto',
  'https://app.example.com/api/v1/dashboard',
  'https://cdn.example.com/images/hero.webp',
  'https://app.example.com/api/v1/settings',
  'https://third-party.cdn.com/widget.js',
  'https://app.example.com/api/v1/notifications',
  'https://cdn.example.com/fonts/custom.woff2',
  'https://app.example.com/api/v1/session/heartbeat',
];
const DEMO_TYPES = [
  'xhr', 'xhr', 'script', 'stylesheet', 'image',
  'xhr', 'image', 'stylesheet', 'xhr', 'image',
  'xhr', 'script', 'xhr', 'font', 'xhr',
];
const DEMO_METHODS = [
  'POST', 'GET', 'GET', 'GET', 'GET',
  'POST', 'GET', 'GET', 'GET', 'GET',
  'GET', 'GET', 'GET', 'GET', 'POST',
];
const DEMO_STATUSES = [
  200, 200, 200, 200, 200,
  204, 200, 200, 200, 200,
  200, 200, 200, 200, 204,
];
const DEMO_SCRIPT_STEPS = [
  { step: 1, action: 'navigate', detail: 'Go to login page', status: 'pending' },
  { step: 2, action: 'fill', detail: 'Enter username', status: 'pending' },
  { step: 3, action: 'fill', detail: 'Enter password', status: 'pending' },
  { step: 4, action: 'click', detail: 'Click Sign In button', status: 'pending' },
  { step: 5, action: 'wait', detail: 'Wait for dashboard load', status: 'pending' },
  { step: 6, action: 'click', detail: 'Navigate to settings', status: 'pending' },
  { step: 7, action: 'verify', detail: 'Confirm logged in state', status: 'pending' },
];

const TYPE_LABELS = {
  xhr: 'XHR',
  script: 'JS',
  stylesheet: 'CSS',
  image: 'Img',
  font: 'Font',
  media: 'Media',
  document: 'Doc',
};

let demoRequestId = 0;

function getTypeLabel(type) {
  return TYPE_LABELS[(type || '').toLowerCase()] || 'Other';
}

function formatMs(ms) {
  if (!ms) return '0s';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

// -- Rule Editor Dialog --
function RuleEditorDialog({ open, onClose, onSave, initialRule }) {
  const [pattern, setPattern] = useState('');
  const [ruleType, setRuleType] = useState('block');
  const [regexError, setRegexError] = useState('');

  useEffect(() => {
    if (initialRule) {
      setPattern(initialRule.pattern || '');
      setRuleType(initialRule.type || 'block');
    } else {
      setPattern('');
      setRuleType('block');
    }
    setRegexError('');
  }, [initialRule, open]);

  const handlePatternChange = (e) => {
    const val = e.target.value;
    setPattern(val);
    try {
      if (val) new RegExp(val);
      setRegexError('');
    } catch (err) {
      setRegexError(err.message);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initialRule ? 'Edit Rule' : 'Add Rule'}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="URL Pattern (regex)"
          fullWidth
          value={pattern}
          onChange={handlePatternChange}
          error={!!regexError}
          helperText={regexError || 'Regular expression to match request URLs'}
          sx={{ mt: 1 }}
          InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
        />
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel>Rule Type</InputLabel>
          <Select value={ruleType} onChange={(e) => setRuleType(e.target.value)} label="Rule Type">
            <MenuItem value="block">Block</MenuItem>
            <MenuItem value="allow">Allow</MenuItem>
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={() => onSave({ pattern, type: ruleType })} variant="contained" disabled={!pattern || !!regexError}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// -- Main Component --
export default function FilterSimulatorPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    targetUrl,
    selectedScriptId,
    selectedRuleSetIds,
    filtersEnabled,
    simulationStatus,
    networkResults,
    elementRules,
    scriptSteps,
    conflictingFilters,
    stats,
    activeTab,
    editorRules,
    testPattern,
    availableRuleSets,
    availableScripts,
    loading,
    error,
    simulationTime,
    compareMode,
  } = useSelector((state) => state.filterSimulator);

  const [urlInput, setUrlInput] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [extensionConnected, setExtensionConnected] = useState(false);
  const [blockerRuleCount, setBlockerRuleCount] = useState(0);
  const simulationInterval = useRef(null);
  const simulationStart = useRef(null);
  const demoIndex = useRef(0);

  const showMessage = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  // Fetch available rule sets and scripts on mount, and detect extension
  useEffect(() => {
    dispatch(fetchRuleSets());
    dispatch(fetchExtensionScripts());

    // Detect extension for real capture
    networkMonitorService.connect()
      .then(() => {
        setExtensionConnected(networkMonitorService.mode === 'extension');
        // Fetch initial blocker status
        networkMonitorService.getBlockerStatus().then((status) => {
          setBlockerRuleCount(status.ruleCount || 0);
        });
      })
      .catch(() => {
        setExtensionConnected(false);
      });

    return () => {
      networkMonitorService.disconnect();
    };
  }, [dispatch]);

  // Check which editor rules match the test pattern
  const testPatternMatches = useMemo(() => {
    if (!testPattern) return {};
    const result = {};
    editorRules.forEach((rule) => {
      try {
        const re = new RegExp(rule.pattern);
        result[rule.id] = re.test(testPattern);
      } catch {
        result[rule.id] = false;
      }
    });
    return result;
  }, [testPattern, editorRules]);

  // Count live matches per editor rule
  const ruleMatchCounts = useMemo(() => {
    const counts = {};
    editorRules.forEach((rule) => {
      try {
        const re = new RegExp(rule.pattern);
        counts[rule.id] = networkResults.filter((r) => re.test(r.url)).length;
      } catch {
        counts[rule.id] = 0;
      }
    });
    return counts;
  }, [editorRules, networkResults]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (simulationInterval.current) clearInterval(simulationInterval.current);
    };
  }, []);

  // Apply editor rules to a single request, return { blocked, matchedRule }
  const applyEditorRulesToRequest = useCallback((url) => {
    let blocked = false;
    let matchedRule = null;
    if (filtersEnabled) {
      for (const rule of editorRules) {
        try {
          const re = new RegExp(rule.pattern);
          if (re.test(url)) {
            if (rule.type === 'block') {
              blocked = true;
              matchedRule = rule.pattern;
            }
            break;
          }
        } catch {
          if (url.includes(rule.pattern)) {
            if (rule.type === 'block') {
              blocked = true;
              matchedRule = rule.pattern;
            }
            break;
          }
        }
      }
    }
    return { blocked, matchedRule };
  }, [filtersEnabled, editorRules]);

  const handleLoadAndSimulate = () => {
    if (!urlInput.trim()) return;
    dispatch(setTargetUrl(urlInput.trim()));
    dispatch(clearNetworkResults());
    dispatch(clearConflictingFilters());
    dispatch(setSimulationStatus('running'));
    simulationStart.current = Date.now();
    demoIndex.current = 0;
    demoRequestId = 0;

    // If a script is selected, set up the steps
    if (selectedScriptId) {
      dispatch(setScriptSteps(DEMO_SCRIPT_STEPS.map((s) => ({ ...s }))));
    }

    if (extensionConnected) {
      // -- Real extension mode: start capturing via the extension --
      networkMonitorService.startCapture();

      const handleRealRequest = (raw) => {
        const url = raw.url || '';
        const { blocked, matchedRule } = applyEditorRulesToRequest(url);
        dispatch(addNetworkResult({
          id: raw.id || `ext-${++demoRequestId}`,
          url,
          method: raw.method || 'GET',
          type: raw.type || raw.resourceType || 'other',
          status: blocked ? null : (raw.status ?? raw.statusCode ?? null),
          blocked,
          matchedRule,
          size: blocked ? 0 : (raw.size || 0),
          duration: blocked ? 0 : (raw.duration || 0),
        }));
        dispatch(setSimulationTime(Date.now() - simulationStart.current));
      };

      networkMonitorService.onNewRequest(handleRealRequest);

      // Open the target URL in a new tab so traffic is generated
      window.open(urlInput.trim(), '_blank');

      // Store the handler ref for cleanup
      simulationInterval.current = { __extensionHandler: handleRealRequest };
      return;
    }

    // -- Demo mode fallback: generate simulated requests --
    if (simulationInterval.current) clearInterval(simulationInterval.current);
    simulationInterval.current = setInterval(() => {
      if (demoIndex.current >= DEMO_URLS.length) {
        clearInterval(simulationInterval.current);
        simulationInterval.current = null;
        dispatch(setSimulationStatus('complete'));
        dispatch(setSimulationTime(Date.now() - simulationStart.current));

        // Finalize script steps
        if (selectedScriptId) {
          DEMO_SCRIPT_STEPS.forEach((_, idx) => {
            dispatch(updateScriptStep({ index: idx, status: 'pass' }));
          });
        }
        return;
      }

      const idx = demoIndex.current;
      const url = DEMO_URLS[idx];

      // Check against editor rules
      const { blocked, matchedRule } = applyEditorRulesToRequest(url);

      dispatch(addNetworkResult({
        id: `sim-${++demoRequestId}`,
        url,
        method: DEMO_METHODS[idx],
        type: DEMO_TYPES[idx],
        status: blocked ? null : DEMO_STATUSES[idx],
        blocked,
        matchedRule,
        size: blocked ? 0 : Math.floor(Math.random() * 300000) + 1000,
        duration: blocked ? 0 : Math.floor(Math.random() * 800) + 50,
      }));

      // Update script step progress
      if (selectedScriptId) {
        const stepIdx = Math.floor((idx / DEMO_URLS.length) * DEMO_SCRIPT_STEPS.length);
        if (stepIdx < DEMO_SCRIPT_STEPS.length) {
          const stepStatus = blocked ? 'fail' : 'pass';
          dispatch(updateScriptStep({ index: stepIdx, status: stepStatus }));

          if (blocked) {
            dispatch(addConflictingFilter({ stepIndex: stepIdx, rulePattern: matchedRule }));
          }
        }
      }

      demoIndex.current += 1;
      dispatch(setSimulationTime(Date.now() - simulationStart.current));
    }, 400);
  };

  const handleStopSimulation = () => {
    if (simulationInterval.current) {
      if (simulationInterval.current.__extensionHandler) {
        // Extension mode: stop capture and remove listener
        networkMonitorService.offNewRequest(simulationInterval.current.__extensionHandler);
        networkMonitorService.stopCapture();
      } else {
        clearInterval(simulationInterval.current);
      }
      simulationInterval.current = null;
    }
    dispatch(setSimulationStatus('complete'));
    if (simulationStart.current) {
      dispatch(setSimulationTime(Date.now() - simulationStart.current));
    }
  };

  const handleReset = () => {
    if (simulationInterval.current) {
      if (simulationInterval.current.__extensionHandler) {
        networkMonitorService.offNewRequest(simulationInterval.current.__extensionHandler);
        networkMonitorService.stopCapture();
      } else {
        clearInterval(simulationInterval.current);
      }
      simulationInterval.current = null;
    }
    dispatch(resetSimulation());
    setUrlInput('');
  };

  const handleSaveEditorRule = (ruleData) => {
    if (editingRule) {
      dispatch(updateEditorRule({ id: editingRule.id, ...ruleData }));
      showMessage('Rule updated');
    } else {
      dispatch(addEditorRule(ruleData));
      showMessage('Rule added');
    }
    setRuleDialogOpen(false);
    setEditingRule(null);
  };

  const handleEditRule = (rule) => {
    setEditingRule(rule);
    setRuleDialogOpen(true);
  };

  const handleMoveRule = (index, direction) => {
    const newRules = [...editorRules];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newRules.length) return;
    const temp = newRules[targetIndex];
    newRules[targetIndex] = newRules[index];
    newRules[index] = temp;
    dispatch(reorderEditorRules(newRules));
  };

  // Load rules from selected rule sets into editor
  useEffect(() => {
    if (selectedRuleSetIds.length > 0 && availableRuleSets.length > 0) {
      const rules = [];
      let id = 1000;
      selectedRuleSetIds.forEach((setId) => {
        const ruleSet = availableRuleSets.find((rs) => rs.id === setId);
        if (ruleSet && ruleSet.rules) {
          ruleSet.rules.forEach((r) => {
            rules.push({
              id: id++,
              pattern: r.pattern || r.urlPattern || '',
              type: (r.action || 'block').toLowerCase(),
              priority: rules.length + 1,
            });
          });
        }
      });
      if (rules.length > 0) {
        dispatch(setEditorRules(rules));
      }
    }
  }, [selectedRuleSetIds, availableRuleSets, dispatch]);

  const scriptPassCount = scriptSteps.filter((s) => s.status === 'pass').length;
  const scriptFailCount = scriptSteps.filter((s) => s.status === 'fail').length;
  const scriptResult = scriptSteps.length > 0
    ? (scriptFailCount > 0 ? 'FAIL' : scriptPassCount === scriptSteps.length ? 'PASS' : 'RUNNING')
    : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 100px)' }}>
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      {/* Top Control Bar */}
      <Paper sx={{ mb: 1 }}>
        <Toolbar variant="dense" sx={{ gap: 1, flexWrap: 'wrap', py: 1 }}>
          <TextField
            size="small"
            placeholder="Target URL"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleLoadAndSimulate()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LinkIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 280 }}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={simulationStatus === 'running' ? <StopIcon /> : <PlayIcon />}
            onClick={simulationStatus === 'running' ? handleStopSimulation : handleLoadAndSimulate}
            size="small"
            disabled={!urlInput.trim() && simulationStatus !== 'running'}
          >
            {simulationStatus === 'running' ? 'Stop' : 'Load & Simulate'}
          </Button>

          <Divider orientation="vertical" flexItem />

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Login Script</InputLabel>
            <Select
              value={selectedScriptId || ''}
              onChange={(e) => dispatch(setSelectedScriptId(e.target.value || null))}
              label="Login Script"
            >
              <MenuItem value="">None</MenuItem>
              {availableScripts.map((s) => (
                <MenuItem key={s.id} value={s.id}>{s.name || s.scriptName || `Script ${s.id}`}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Network Rules</InputLabel>
            <Select
              multiple
              value={selectedRuleSetIds}
              onChange={(e) => dispatch(setSelectedRuleSetIds(e.target.value))}
              label="Network Rules"
              renderValue={(selected) => `${selected.length} set(s)`}
            >
              {availableRuleSets.map((rs) => (
                <MenuItem key={rs.id} value={rs.id}>
                  <Checkbox size="small" checked={selectedRuleSetIds.includes(rs.id)} />
                  {rs.name || `Rule Set ${rs.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Chip
            label={`Element Rules: ${elementRules.filter((r) => r.enabled).length}`}
            size="small"
            variant="outlined"
            icon={<VisibilityOffIcon />}
          />

          <Divider orientation="vertical" flexItem />

          <FormControlLabel
            control={
              <Switch
                checked={filtersEnabled}
                onChange={(e) => {
                  dispatch(setFiltersEnabled(e.target.checked));
                  // Also toggle the real content blocker in the extension
                  if (extensionConnected) {
                    networkMonitorService.toggleContentBlocker().then((res) => {
                      if (res.ruleCount !== undefined) setBlockerRuleCount(res.ruleCount);
                      showMessage(res.enabled ? 'Content blocker enabled' : 'Content blocker disabled');
                    });
                  }
                }}
                color={filtersEnabled ? 'success' : 'default'}
              />
            }
            label={
              <Chip
                label={filtersEnabled ? `Filters ON (${blockerRuleCount} DNR)` : 'Filters OFF'}
                color={filtersEnabled ? 'success' : 'error'}
                size="small"
                variant="filled"
              />
            }
          />

          <Box sx={{ flexGrow: 1 }} />

          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ResetIcon />}
            onClick={handleReset}
            size="small"
          >
            Reset
          </Button>
        </Toolbar>
        {simulationStatus === 'running' && <LinearProgress />}
      </Paper>

      {/* Main Content: Left Panel + Right Panel */}
      <Box sx={{ display: 'flex', flexGrow: 1, gap: 1, overflow: 'hidden' }}>

        {/* Left Panel (60%): Simulation Results */}
        <Paper sx={{ flex: '0 0 60%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => dispatch(setActiveTab(v))}
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Network Requests" />
            <Tab label="Hidden Elements" />
            <Tab label="Script Playback" />
          </Tabs>

          {/* Tab 1: Network Requests */}
          {activeTab === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' }}>
              {/* Stats bar */}
              <Box sx={{ px: 2, py: 1, display: 'flex', gap: 2, alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="body2">
                  <Typography component="span" variant="body2" color="success.main" fontWeight="bold">{stats.allowed}</Typography> allowed,{' '}
                  <Typography component="span" variant="body2" color="error.main" fontWeight="bold">{stats.blocked}</Typography> blocked,{' '}
                  <Typography component="span" variant="body2" color="warning.main" fontWeight="bold">{stats.errors}</Typography> errors
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={compareMode}
                      onChange={(e) => dispatch(setCompareMode(e.target.checked))}
                    />
                  }
                  label={<Typography variant="caption">Compare</Typography>}
                />
              </Box>

              {compareMode ? (
                /* Compare mode: side-by-side */
                <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
                  <Box sx={{ flex: 1, overflow: 'auto', borderRight: 1, borderColor: 'divider' }}>
                    <Typography variant="subtitle2" sx={{ px: 2, py: 1, bgcolor: 'success.light', color: 'success.contrastText' }}>
                      With Filters ({networkResults.filter((r) => !r.blocked).length} allowed)
                    </Typography>
                    <TableContainer>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: 30 }}>St</TableCell>
                            <TableCell>URL</TableCell>
                            <TableCell sx={{ width: 50 }}>Type</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {networkResults.filter((r) => !r.blocked).map((req) => (
                            <TableRow key={req.id} hover>
                              <TableCell>
                                <CircleIcon sx={{ fontSize: 10, color: 'success.main' }} />
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }} noWrap>
                                  {req.url}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption">{getTypeLabel(req.type)}</Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                  <Box sx={{ flex: 1, overflow: 'auto' }}>
                    <Typography variant="subtitle2" sx={{ px: 2, py: 1, bgcolor: 'error.light', color: 'error.contrastText' }}>
                      Blocked ({networkResults.filter((r) => r.blocked).length})
                    </Typography>
                    <TableContainer>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: 30 }}>St</TableCell>
                            <TableCell>URL</TableCell>
                            <TableCell>Rule</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {networkResults.filter((r) => r.blocked).map((req) => (
                            <TableRow key={req.id} hover>
                              <TableCell>
                                <BlockIcon sx={{ fontSize: 14, color: 'error.main' }} />
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', textDecoration: 'line-through' }} noWrap>
                                  {req.url}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.65rem' }} noWrap>
                                  {req.matchedRule || '-'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Box>
              ) : (
                /* Normal view */
                <TableContainer sx={{ flexGrow: 1, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 40 }}>Status</TableCell>
                        <TableCell>URL</TableCell>
                        <TableCell sx={{ width: 50 }}>Type</TableCell>
                        <TableCell sx={{ width: 180 }}>Rule Matched</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {networkResults.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                              No simulation results yet. Enter a URL and click "Load & Simulate".
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        networkResults.map((req) => (
                          <TableRow
                            key={req.id}
                            hover
                            sx={{
                              bgcolor: req.blocked ? 'error.light' : 'inherit',
                              opacity: req.blocked ? 0.8 : 1,
                            }}
                          >
                            <TableCell>
                              <CircleIcon
                                sx={{
                                  fontSize: 12,
                                  color: req.blocked ? 'error.main' : req.status == null ? 'grey.400' : 'success.main',
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Tooltip title={req.url} placement="bottom-start">
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontFamily: 'monospace',
                                    fontSize: '0.75rem',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: 400,
                                    textDecoration: req.blocked ? 'line-through' : 'none',
                                  }}
                                >
                                  {req.url}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">
                                {getTypeLabel(req.type)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {req.matchedRule ? (
                                <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'error.main' }}>
                                  {req.matchedRule}
                                </Typography>
                              ) : (
                                <Typography variant="caption" color="text.disabled">-</Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* Tab 2: Hidden Elements */}
          {activeTab === 1 && (
            <Box sx={{ p: 2, overflow: 'auto', flexGrow: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  CSS Element Hide Rules
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const selector = window.prompt('Enter CSS selector:');
                      if (selector && selector.trim()) {
                        dispatch(addElementRule({ selector: selector.trim(), matchCount: 0 }));
                      }
                    }}
                  >
                    Add Rule
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<LinkIcon />}
                    onClick={() => navigate('/element-hider')}
                  >
                    Add from Element Hider
                  </Button>
                </Box>
              </Box>

              {elementRules.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No element hide rules configured. Add rules or import from Element Hider.
                </Typography>
              ) : (
                <List dense>
                  {elementRules.map((rule) => (
                    <ListItem key={rule.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 1 }}>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {rule.selector}
                          </Typography>
                        }
                        secondary={`${rule.matchCount} matched element(s)`}
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title={rule.enabled ? 'Disable' : 'Enable'}>
                          <IconButton size="small" onClick={() => dispatch(toggleElementRule(rule.id))}>
                            {rule.enabled ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove">
                          <IconButton size="small" color="error" onClick={() => dispatch(removeElementRule(rule.id))}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}

          {/* Tab 3: Script Playback */}
          {activeTab === 2 && (
            <Box sx={{ p: 2, overflow: 'auto', flexGrow: 1 }}>
              {!selectedScriptId ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  Select a login script from the control bar to see playback steps.
                </Typography>
              ) : scriptSteps.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  Run the simulation to see script playback.
                </Typography>
              ) : (
                <>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: 50 }}>Step</TableCell>
                          <TableCell sx={{ width: 100 }}>Action</TableCell>
                          <TableCell>Detail</TableCell>
                          <TableCell sx={{ width: 80 }} align="center">Status</TableCell>
                          <TableCell sx={{ width: 120 }}>Interference</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {scriptSteps.map((step, idx) => {
                          const conflict = conflictingFilters.find((c) => c.stepIndex === idx);
                          return (
                            <TableRow key={idx} hover>
                              <TableCell>
                                <Typography variant="body2" fontWeight="bold">{step.step}</Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={step.action}
                                  size="small"
                                  variant="outlined"
                                  color={step.action === 'navigate' ? 'primary' : step.action === 'click' ? 'secondary' : 'default'}
                                  sx={{ fontFamily: 'monospace', fontSize: '0.7rem', height: 22 }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{step.detail}</Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={step.status}
                                  size="small"
                                  color={step.status === 'pass' ? 'success' : step.status === 'fail' ? 'error' : 'default'}
                                  variant="filled"
                                  sx={{ fontSize: '0.7rem', height: 22 }}
                                />
                              </TableCell>
                              <TableCell>
                                {conflict ? (
                                  <Tooltip title={`Filter "${conflict.rulePattern}" blocked a required resource`}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <WarningIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                                      <Button
                                        size="small"
                                        variant="text"
                                        color="warning"
                                        sx={{ fontSize: '0.65rem', textTransform: 'none', p: 0, minWidth: 'auto' }}
                                        onClick={() => {
                                          const rule = editorRules.find((r) => r.pattern === conflict.rulePattern);
                                          if (rule) {
                                            dispatch(removeEditorRule(rule.id));
                                            showMessage(`Disabled conflicting filter: ${conflict.rulePattern}`);
                                          }
                                        }}
                                      >
                                        Disable
                                      </Button>
                                    </Box>
                                  </Tooltip>
                                ) : (
                                  <Typography variant="caption" color="text.disabled">-</Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>
          )}
        </Paper>

        {/* Right Panel (40%): Rule Editor */}
        <Paper sx={{ flex: '0 0 calc(40% - 8px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Rule Editor
            </Typography>
            <IconButton
              size="small"
              color="primary"
              onClick={() => {
                setEditingRule(null);
                setRuleDialogOpen(true);
              }}
            >
              <AddIcon />
            </IconButton>
          </Box>

          {/* Test Pattern */}
          <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Test Pattern - type a URL to check matches"
              value={testPattern}
              onChange={(e) => dispatch(setTestPattern(e.target.value))}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
              }}
            />
          </Box>

          {/* Rules List */}
          <Box sx={{ flexGrow: 1, overflow: 'auto', px: 1 }}>
            {editorRules.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4, px: 2 }}>
                No rules defined. Click + to add, or select a Network Rule Set above.
              </Typography>
            ) : (
              <List dense disablePadding>
                {editorRules.map((rule, index) => {
                  const testMatch = testPatternMatches[rule.id];
                  const matchCount = ruleMatchCounts[rule.id] || 0;
                  return (
                    <React.Fragment key={rule.id}>
                      <ListItem
                        sx={{
                          px: 1,
                          py: 0.5,
                          bgcolor: testPattern && testMatch ? (rule.type === 'block' ? 'error.light' : 'success.light') : 'inherit',
                          borderRadius: 1,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                          {rule.type === 'block' ? (
                            <BlockIcon sx={{ fontSize: 16, color: 'error.main' }} />
                          ) : (
                            <AllowIcon sx={{ fontSize: 16, color: 'success.main' }} />
                          )}
                        </Box>
                        <ListItemText
                          primary={
                            <Typography
                              variant="body2"
                              sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}
                            >
                              {rule.pattern}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {rule.type} | P{rule.priority} | {matchCount} matched
                              {testPattern && testMatch !== undefined && (
                                <Typography component="span" variant="caption" color={testMatch ? 'error.main' : 'text.disabled'}>
                                  {' '}| test: {testMatch ? 'MATCH' : 'no match'}
                                </Typography>
                              )}
                            </Typography>
                          }
                        />
                        <ListItemSecondaryAction>
                          <Tooltip title="Move up">
                            <span>
                              <IconButton size="small" onClick={() => handleMoveRule(index, -1)} disabled={index === 0}>
                                <UpIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Move down">
                            <span>
                              <IconButton size="small" onClick={() => handleMoveRule(index, 1)} disabled={index === editorRules.length - 1}>
                                <DownIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={rule.type === 'block' ? 'Switch to Allow' : 'Switch to Block'}>
                            <IconButton
                              size="small"
                              onClick={() => dispatch(updateEditorRule({
                                id: rule.id,
                                type: rule.type === 'block' ? 'allow' : 'block',
                              }))}
                            >
                              {rule.type === 'block' ? (
                                <ToggleOffIcon sx={{ fontSize: 16, color: 'error.main' }} />
                              ) : (
                                <ToggleOnIcon sx={{ fontSize: 16, color: 'success.main' }} />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleEditRule(rule)}>
                              <EditIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => dispatch(removeEditorRule(rule.id))}>
                              <DeleteIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </ListItemSecondaryAction>
                      </ListItem>
                      {index < editorRules.length - 1 && <Divider />}
                    </React.Fragment>
                  );
                })}
              </List>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Bottom Status Bar */}
      <Paper sx={{ mt: 1, px: 2, py: 0.75, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CircleIcon
            sx={{
              fontSize: 8,
              color: simulationStatus === 'running' ? 'success.main' : simulationStatus === 'complete' ? 'primary.main' : 'grey.400',
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {simulationStatus === 'running' ? 'Running' : simulationStatus === 'complete' ? 'Complete' : 'Idle'}
          </Typography>
        </Box>
        <Divider orientation="vertical" flexItem />
        <Typography variant="caption" color="text.secondary">
          Time: {formatMs(simulationTime)}
        </Typography>
        <Divider orientation="vertical" flexItem />
        <Typography variant="caption" color="text.secondary">
          Mode: {extensionConnected ? 'Extension' : 'Demo'} |{' '}
          {networkResults.length} requests, {stats.blocked} blocked, {stats.elementsHidden} elements hidden
          {scriptResult && (
            <Typography
              component="span"
              variant="caption"
              color={scriptResult === 'PASS' ? 'success.main' : scriptResult === 'FAIL' ? 'error.main' : 'text.secondary'}
              fontWeight="bold"
            >
              {' '}| Login script: {scriptResult}
            </Typography>
          )}
        </Typography>
      </Paper>

      {/* Rule Editor Dialog */}
      <RuleEditorDialog
        open={ruleDialogOpen}
        onClose={() => {
          setRuleDialogOpen(false);
          setEditingRule(null);
        }}
        onSave={handleSaveEditorRule}
        initialRule={editingRule}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
